import type React from 'react';
import type { FC } from 'react';

import classNames from 'classnames';

import type { ObjectType } from '@iconik-io/iconik-api/src/types/object_types';
import type { SearchItemEntity } from '@iconik-io/shared-lib/src/redux/entities/normalized/search_item';
import { updateEntities } from '@iconik-io/shared-lib/src/redux/entities/slice';

import { modifyBranchByID } from '../../ducks/tree/thunks';
import { useAppDispatch } from '../../store/react_redux_typed';
import OpenContextMenuButton from '../../ui/table/open_context_menu_button/open_context_menu_button';
import { parseObjectIconInfo } from '../../utils/parse_object_icon_info';
import Ellipsis from '../ellipsis/ellipsis';
import { FavouriteButton } from '../favourite_button/favourite_button';
import { Link } from '../link/link';
import { ObjectIcon } from '../object_icon/object_icon';

import styles from './search_pod_title.module.css';

type Props = {
  isSelected?: boolean;
  isDropTarget?: boolean;
  isDroppable?: boolean;
  searchItem: SearchItemEntity;
  objectType: 'asset' | 'collection';
  hideContextMenu?: boolean;
  hideFavouriteButton?: boolean;
  getObjectLink?: (
    id: string,
    objectType: ObjectType,
    versionId?: string,
  ) => string;
  onTitleLinkClick?: (item: SearchItemEntity, event: React.MouseEvent) => void;
};

const SearchPodTitle: FC<Props> = (props) => {
  const {
    searchItem,
    getObjectLink,
    onTitleLinkClick,
    hideContextMenu = false,
    hideFavouriteButton = false,
  } = props;
  const link = getObjectLink?.(searchItem.id, searchItem.object_type);
  const dispatch = useAppDispatch();
  const objectIconInfo = parseObjectIconInfo(searchItem);
  const hasFormatObjectIcon =
    'format' in objectIconInfo && !('type' in objectIconInfo);

  const title = (
    <Ellipsis
      componentProps={{ className: styles.wrap_text }}
      tooltipText={searchItem.title}
    >
      <ObjectIcon
        className={classNames(styles.title_icon, {
          [styles.title_icon_format]: hasFormatObjectIcon,
        })}
        info={objectIconInfo}
        textAlign
      />
      {searchItem.title}
    </Ellipsis>
  );

  return (
    <span className={styles.root}>
      <div className={styles.title_group}>
        {link ? (
          <Link
            className={styles.title}
            to={link}
            onClick={(event) => {
              onTitleLinkClick?.(searchItem, event);
            }}
          >
            {title}
          </Link>
        ) : (
          <div className={styles.title}>{title}</div>
        )}
        {!hideFavouriteButton ? (
          <FavouriteButton
            onClick={(favoured) => {
              dispatch(
                updateEntities({
                  search_items: {
                    [searchItem.id]: { favoured },
                  },
                }),
              );
              if (searchItem.object_type === 'collections') {
                dispatch(
                  modifyBranchByID('COLLECTION', searchItem.id, { favoured }),
                );
              }
            }}
            id={searchItem.id}
            title={searchItem.title}
            objectType={searchItem.object_type}
            favoured={Boolean(searchItem.favoured)}
            className={styles.favourite_button}
          />
        ) : null}
        {!hideContextMenu ? <OpenContextMenuButton /> : null}
      </div>
    </span>
  );
};

export default SearchPodTitle;
